/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Login` for the Caintra Connect frontend.
 * @module pages/Login
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import { useAuthSession } from "../context/AuthSessionContext";
import { handleSSOLogin, getUserRoleData } from "../logic/auth/index.js";
import styles from "../styles/Login.module.css";
import Caintra_Logo from "../assets/Caintra_Logo.png";
import SmallLoginBackground from "../assets/SmallLoginBackground.png";
import { IconButton, Divider, CircularProgress } from "@mui/material";
import Visibility from "@mui/icons-material/VisibilityOutlined";
import VisibilityOff from "@mui/icons-material/VisibilityOffOutlined";

const Login = () => {
  const { establishSession } = useAuthSession();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isTablet, setIsTablet] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [values, setValues] = useState({
    email: "",
    password: "",
    showPassword: false,
    showError: false,
    errorType: "default",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 768 && width <= 1023);
      setIsMobile(width < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const getBackgroundStyle = () => {
    if (isTablet || isMobile) {
      return {
        backgroundImage: `url(${SmallLoginBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    }
    return {};
  };

  const handleInputChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const loginUser = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const authParams = {
      email: values.email,
      password: values.password,
    };
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(authParams),
    };
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}auth/session`,
        options,
      );
      const data = await response.json();
      if (!response.ok) {
        if (
          response.status === 403 &&
          data?.message === "User account is inactive"
        ) {
          setValues({
            ...values,
            showError: true,
            errorType: "deactivated",
          });
          return;
        }
        setValues({
          ...values,
          showError: true,
          errorType: "default",
        });
        return;
      }

      localStorage.setItem("userId", data.userId);
      localStorage.setItem("token", data.AccessToken);
      localStorage.setItem("refreshToken", data.RefreshToken);

      const userRoleData = await getUserRoleData({
        token: data.AccessToken,
        userId: data.userId,
      });

      await establishSession({ permissions: userRoleData?.permisos ?? null });

      window.dataLayer.push({
        event: "login",
        user_id: data.userId,
      });

      setValues({
        ...values,
        showError: false,
        email: "",
        password: "",
      });
      navigate("/home");
    } catch (error) {
      logger.error("Error:", { err: sanitizeError(error) });
      setValues({ ...values, showError: true, errorType: "default" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isButtonDisabled =
    values.email.trim() === "" || values.password.trim() === "";

  return (
    <div
      className={`${styles.container} ${
        !isTablet && !isMobile ? styles.desktopBackground : ""
      }`}
      style={getBackgroundStyle()}
    >
      <div className={styles.header}>
        <LanguageSelector />
      </div>
      <div className={styles.main}>
        <div className={styles.logoContainer}>
          <img src={Caintra_Logo} alt="" />
        </div>
        {!isTablet && !isMobile && (
          <Divider
            aria-hidden="true"
            variant="middle"
            orientation="vertical"
            sx={{ borderColor: "#1D5AE8", height: "90%" }}
          />
        )}
        <div className={styles.formContainer}>
          <div className={styles.formContent}>
            <div>
              <span className={styles.title}>{t("pages.login.header")}</span>
            </div>
            {(isTablet || isMobile) && (
              <Divider
                aria-hidden="true"
                variant="middle"
                orientation="horizontal"
                className={
                  isMobile
                    ? styles.horizontalDividerMobile
                    : styles.horizontalDividerTablet
                }
              />
            )}
            <div className={styles.formFields}>
              <div className={styles.formFieldsInner}>
                <div className={styles.fieldGroup}>
                  <span className={styles.subtitle}>
                    {t("pages.login.username")}
                  </span>
                  <div className={styles.inputContainer}>
                    <input
                      type="text"
                      placeholder={t("pages.login.usernamePlaceholder")}
                      onChange={handleInputChange("email")}
                      value={values.email}
                    />
                  </div>
                </div>
                <div
                  className={`${styles.fieldGroup} ${styles.passwordFieldGroup}`}
                >
                  <div className={styles.passwordHeader}>
                    <span className={styles.subtitle}>
                      {t("pages.login.password")}
                    </span>
                    <span
                      className={`${styles.subtitle} ${styles.forgotPassword}`}
                      onClick={() => navigate("/passwordRecovery")}
                    >
                      {t("pages.login.forgotPassword")}
                    </span>
                  </div>
                  <div className={styles.inputContainer}>
                    <input
                      className={styles.inputPassword}
                      type={values.showPassword ? "text" : "password"}
                      placeholder={t("pages.login.password")}
                      onChange={handleInputChange("password")}
                      value={values.password}
                    />
                    <IconButton onClick={handleClickShowPassword}>
                      {values.showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </div>
                </div>
              </div>
              {values.showError && (
                <div className={styles.errorContainer}>
                  <span className={styles.errorMessage}>
                    {values.errorType === "deactivated"
                      ? t("pages.login.accountDeactivatedMessage")
                      : t("pages.login.errorMessage")}
                  </span>
                </div>
              )}
            </div>
            <div className={styles.formActions}>
              <button
                className={styles.submitButton}
                type="submit"
                onClick={loginUser}
                disabled={isButtonDisabled || isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("pages.login.login")
                )}
              </button>
              <div className={styles.registerSection}>
                <span className={styles.registerText}>
                  {t("pages.login.registerText01")}{" "}
                  <span
                    className={styles.registerLink}
                    onClick={() => navigate("/register")}
                  >
                    {t("pages.login.registerText02")}
                  </span>
                </span>
                <Divider
                  aria-hidden="true"
                  variant="fullWidth"
                  orientation="horizontal"
                  className={styles.horizonDivider}
                />
                <button
                  className={styles.ssoButton}
                  type="submit"
                  onClick={handleSSOLogin}
                >
                  {t("pages.login.ssoLogin")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
